import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { profileAPI, analyticsAPI } from '@/lib/api';
import { 
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  MessageCircle,
  ExternalLink,
  Instagram,
  Twitter,
  Linkedin,
  Facebook,
  Youtube,
  Github,
  Music2,
  AtSign,
  Twitch,
  Send,
  Link2,
  CreditCard,
  X,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';

const SOCIAL_ICONS = {
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  youtube: Youtube,
  github: Github,
  tiktok: Music2,
  threads: AtSign,
  twitch: Twitch,
  telegram: Send,
  other: Link2
};

const getSocialIcon = (platform) => SOCIAL_ICONS[platform] || Link2;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileAPI.getPublic(slug);
        setProfile(response.data);
        // Track profile view after data loads
        analyticsAPI.track(slug, 'profile_view');
      } catch (err) {
        setError(err.response?.status === 404 ? 'Profile not found' : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-slate-600" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{error || 'Profile Not Found'}</h1>
          <p className="text-slate-400 mb-6">This profile doesn't exist or has been removed.</p>
          <Link to="/" className="text-violet-400 hover:text-violet-300">
            ← Back to MeTag
          </Link>
        </div>
      </div>
    );
  }

  const openWhatsApp = () => {
    if (profile.whatsapp) {
      analyticsAPI.track(slug, 'whatsapp_click');
      const number = profile.whatsapp.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${number}`, '_blank');
    }
  };

  // Get theme with defaults
  const theme = profile.theme || {
    primaryColor: '#7c3aed',
    backgroundColor: '#020617',
    textColor: '#f8fafc',
    accentColor: '#a855f7',
    fontFamily: 'Inter'
  };

  // SEO Meta Data
  const siteUrl = 'https://metag.cc';
  const pageUrl = `${siteUrl}/p/${slug}`;
  const displayName = profile.displayName || 'User';
  const shortBio = profile.shortBio || '';
  const longBio = profile.longBio || '';
  const profileImage = profile.profilePicture || `${siteUrl}/default-avatar.png`;
  
  // Construct SEO title
  const pageTitle = shortBio 
    ? `${displayName} | ${shortBio} | MeTag - Tap-Connect-Share`
    : `${displayName} | MeTag - Tap-Connect-Share`;
  
  // Construct SEO description
  const pageDescription = longBio 
    ? `${shortBio ? shortBio + '. ' : ''}${longBio.substring(0, 150)}...`
    : shortBio || `Connect with ${displayName} on MeTag. Tap to share contact information instantly.`;

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={profileImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="MeTag" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={profileImage} />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content={displayName} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": displayName,
            "description": shortBio || longBio,
            "image": profileImage,
            "url": pageUrl,
            ...(profile.email && { "email": profile.email }),
            ...(profile.phone && { "telephone": profile.phone }),
            ...(profile.address && { "address": profile.address }),
            ...(profile.website && { "url": profile.website })
          })}
        </script>
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor, fontFamily: theme.fontFamily }} data-testid="public-profile">
      {/* Cover Photo / Header Background */}
      <div className="h-48 relative overflow-hidden">
        {profile.coverPhoto ? (
          <img
            src={profile.coverPhoto}
            alt="Cover"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="h-full" style={{ background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})` }} />
        )}
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Profile Content */}
      <div className="max-w-2xl mx-auto px-4 -mt-20 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Profile Picture */}
          <div className="relative inline-block mb-4">
            {profile.profilePicture ? (
              <img
                src={profile.profilePicture}
                alt={profile.displayName || 'Profile'}
                className="w-32 h-32 rounded-full object-cover shadow-xl"
                style={{ border: `4px solid ${theme.backgroundColor}` }}
              />
            ) : (
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
                  border: `4px solid ${theme.backgroundColor}`
                }}
              >
                <User className="w-16 h-16 text-white" />
              </div>
            )}
          </div>

          {/* Name & Bio */}
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.textColor }}>{profile.displayName || 'Anonymous'}</h1>
          {profile.shortBio && (
            <p className="text-lg mb-6" style={{ color: theme.textColor, opacity: 0.7 }}>{profile.shortBio}</p>
          )}

          {/* Social Links */}
          {profile.socialLinks && profile.socialLinks.length > 0 && (
            <div className="flex justify-center gap-3 mb-8">
              {profile.socialLinks.map((link, index) => {
                const Icon = getSocialIcon(link.platform);
                return (
                  <motion.a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => analyticsAPI.track(slug, 'social_click', { platform: link.platform })}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all group"
                    style={{ 
                      backgroundColor: `${theme.primaryColor}20`,
                      border: `1px solid ${theme.primaryColor}40`
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    title={link.label || link.platform}
                  >
                    <Icon className="w-5 h-5 transition-colors" style={{ color: theme.accentColor }} />
                  </motion.a>
                );
              })}
            </div>
          )}

          {/* Long Bio */}
          {profile.longBio && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 mb-6 text-left"
              style={{ 
                backgroundColor: `${theme.primaryColor}10`,
                border: `1px solid ${theme.primaryColor}30`
              }}
            >
              <h2 className="text-lg font-semibold mb-3" style={{ color: theme.textColor }}>About</h2>
              <p className="leading-relaxed whitespace-pre-line" style={{ color: theme.textColor, opacity: 0.8 }}>{profile.longBio}</p>
            </motion.div>
          )}

          {/* Contact Buttons */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {profile.phone && (
              <a
                href={`tel:${profile.phone}`}
                onClick={() => analyticsAPI.track(slug, 'phone_click')}
                className="rounded-xl p-4 flex items-center gap-3 transition-all hover:opacity-80"
                style={{ 
                  backgroundColor: `${theme.primaryColor}15`,
                  border: `1px solid ${theme.primaryColor}30`
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.accentColor}30` }}>
                  <Phone className="w-5 h-5" style={{ color: theme.accentColor }} />
                </div>
                <div className="text-left">
                  <p className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>Call</p>
                  <p className="text-sm font-medium" style={{ color: theme.textColor }}>{profile.phone}</p>
                </div>
              </a>
            )}

            {profile.whatsapp && (
              <button
                onClick={openWhatsApp}
                className="rounded-xl p-4 flex items-center gap-3 transition-all hover:opacity-80 text-left"
                style={{ 
                  backgroundColor: `${theme.primaryColor}15`,
                  border: `1px solid ${theme.primaryColor}30`
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.accentColor}30` }}>
                  <MessageCircle className="w-5 h-5" style={{ color: theme.accentColor }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>WhatsApp</p>
                  <p className="text-sm font-medium" style={{ color: theme.textColor }}>{profile.whatsapp}</p>
                </div>
              </button>
            )}

            {profile.email && (
              <a
                href={`mailto:${profile.email}`}
                onClick={() => analyticsAPI.track(slug, 'email_click')}
                className="rounded-xl p-4 flex items-center gap-3 transition-all hover:opacity-80"
                style={{ 
                  backgroundColor: `${theme.primaryColor}15`,
                  border: `1px solid ${theme.primaryColor}30`
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.accentColor}30` }}>
                  <Mail className="w-5 h-5" style={{ color: theme.accentColor }} />
                </div>
                <div className="text-left">
                  <p className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>Email</p>
                  <p className="text-sm truncate font-medium" style={{ color: theme.textColor }}>{profile.email}</p>
                </div>
              </a>
            )}

            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analyticsAPI.track(slug, 'website_click')}
                className="rounded-xl p-4 flex items-center gap-3 transition-all hover:opacity-80"
                style={{ 
                  backgroundColor: `${theme.primaryColor}15`,
                  border: `1px solid ${theme.primaryColor}30`
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.accentColor}30` }}>
                  <Globe className="w-5 h-5" style={{ color: theme.accentColor }} />
                </div>
                <div className="text-left">
                  <p className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>Website</p>
                  <p className="text-sm truncate font-medium" style={{ color: theme.textColor }}>{profile.website.replace(/https?:\/\//, '')}</p>
                </div>
              </a>
            )}
          </div>

          {/* Address */}
          {profile.address && (
            <motion.a
              href={
                profile.latitude && profile.longitude
                  ? `https://maps.google.com?q=${profile.latitude},${profile.longitude}`
                  : `https://maps.google.com?q=${encodeURIComponent([profile.displayName, profile.address].filter(Boolean).join(', '))}`
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analyticsAPI.track(slug, 'address_click')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl p-4 flex items-center gap-3 mb-6 transition-all hover:opacity-80 cursor-pointer"
              style={{ 
                backgroundColor: `${theme.primaryColor}15`,
                border: `1px solid ${theme.primaryColor}30`,
                display: 'flex'
              }}
              data-testid="profile-address-link"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme.accentColor}30` }}>
                <MapPin className="w-5 h-5" style={{ color: theme.accentColor }} />
              </div>
              <div className="text-left flex-1">
                <p className="text-xs" style={{ color: theme.textColor, opacity: 0.6 }}>Address</p>
                <p className="text-sm font-medium" style={{ color: theme.textColor }}>{profile.address}</p>
                {profile.latitude && profile.longitude ? (
                  <p className="text-xs mt-0.5 font-mono" style={{ color: theme.accentColor }}>
                    {parseFloat(profile.latitude).toFixed(6)}°, {parseFloat(profile.longitude).toFixed(6)}° · Open in Maps
                  </p>
                ) : (
                  <p className="text-xs mt-0.5" style={{ color: theme.accentColor, opacity: 0.7 }}>Open in Maps</p>
                )}
              </div>
              <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: theme.textColor, opacity: 0.4 }} />
            </motion.a>
          )}

          {/* Gallery */}
          {profile.images && profile.images.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="rounded-2xl p-6 mb-6"
              style={{ 
                backgroundColor: `${theme.primaryColor}10`,
                border: `1px solid ${theme.primaryColor}30`
              }}
            >
              <h2 className="text-lg font-semibold mb-4 text-left" style={{ color: theme.textColor }}>Gallery</h2>
              <div className="grid grid-cols-3 gap-2">
                {profile.images.map((img, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className="aspect-square rounded-xl overflow-hidden cursor-pointer"
                    style={{ border: `2px solid ${theme.primaryColor}20` }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Image Modal */}
          <AnimatePresence>
            {selectedImageIndex !== null && profile.images && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
                onClick={() => setSelectedImageIndex(null)}
              >
                <button
                  onClick={() => setSelectedImageIndex(null)}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                >
                  <X className="w-8 h-8" />
                </button>

                {profile.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => 
                          prev === 0 ? profile.images.length - 1 : prev - 1
                        );
                      }}
                      className="absolute left-4 text-white hover:text-gray-300 z-10"
                    >
                      <ChevronLeft className="w-10 h-10" />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => 
                          prev === profile.images.length - 1 ? 0 : prev + 1
                        );
                      }}
                      className="absolute right-4 text-white hover:text-gray-300 z-10"
                    >
                      <ChevronRight className="w-10 h-10" />
                    </button>
                  </>
                )}

                <motion.img
                  key={selectedImageIndex}
                  src={profile.images[selectedImageIndex]}
                  alt={`Gallery ${selectedImageIndex + 1}`}
                  className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                />

                <div className="absolute bottom-4 text-white text-sm">
                  {selectedImageIndex + 1} / {profile.images.length}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="pt-8" style={{ borderTop: `1px solid ${theme.primaryColor}30` }}>
            <Link to="/" className="inline-flex items-center gap-2 transition-colors hover:opacity-70" style={{ color: theme.textColor, opacity: 0.5 }}>
              <CreditCard className="w-4 h-4" />
              <span className="text-sm">Powered by MeTag</span>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Floating Save Contact Button */}
      {(profile.phone || profile.email || profile.name || profile.displayName) && (
        <a
          href={`${BACKEND_URL}/api/p/${slug}/vcard`}
          download={`${profile.displayName || slug}.vcf`}
          onClick={() => analyticsAPI.track(slug, 'vcard_download')}
          className="fixed bottom-6 right-6 flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-lg transition-all hover:scale-105 active:scale-95 z-40"
          style={{
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
            color: '#fff',
            boxShadow: `0 4px 24px ${theme.primaryColor}60`
          }}
          data-testid="save-contact-btn"
        >
          <Download className="w-4 h-4" />
          Save Contact
        </a>
      )}
    </div>
    </>
  );
}
